// ============================================================================
// JARVISTRAVEL - CHECKOUT RETURN (JAR-1184)
// Where Stripe sends the buyer back to. Two outcomes, one component.
// ============================================================================

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Check, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/app/components/shared';
import {
  getEntitlement,
  entitlementChanged,
  readEntitlementBeforeCheckout,
  forgetEntitlementBeforeCheckout,
  type Entitlement,
} from '@/app/api/payments';
import { getBackendStatus, subscribeBackendStatus } from '@/app/api/backendStatus';
import { clearPlanIntent } from '@/app/utils/planIntent';

/**
 * How long to keep asking the server whether the purchase has landed.
 *
 * Stripe redirects the buyer back the instant the payment succeeds, but the
 * entitlement is granted by the `checkout.session.completed` webhook arriving
 * separately. The two race, and the redirect usually wins. So an unentitled
 * read here is the expected first answer, not a failure — which is why this
 * screen never says anything went wrong while the window is still open.
 */
const POLL_WINDOW_MS = 20_000;
const POLL_INTERVAL_MS = 1_500;

type Outcome = 'success' | 'canceled';

interface CheckoutReturnProps {
  outcome: Outcome;
  /** Into the app. */
  onContinue: () => void;
}

export function CheckoutReturn({ outcome, onContinue }: CheckoutReturnProps) {
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [stillWaiting, setStillWaiting] = useState(false);
  // A backend we cannot reach is not a webhook that has not landed. Without
  // this the two rendered identically — "still catching up, sign out and in"
  // — for a buyer whose payment went through and whose servers are down.
  // Seeded from the store and kept live by the subscription below: the store
  // flips the moment ANY request fails, which can be well before this screen's
  // own poll returns (the client aborts a hung request at 30s, after the 20s
  // deadline here; OCR MED, web-app#252).
  const [outage, setOutage] = useState(() => getBackendStatus() === 'offline');
  const timers = useRef<number[]>([]);
  // One clock for both deadlines. The poll window used to start when Clerk
  // settled while the wall-clock timer started at mount, so a late-settling
  // Clerk had "sign out and back in" on screen while polling was still
  // legitimately running (OCR LOW, web-app#252).
  const mountedAt = useRef(Date.now());
  // Whether this screen has SENT a poll, and whether any poll has ANSWERED. At
  // the deadline, sent-but-unanswered is a backend we cannot reach, not a
  // webhook that is late; the two get different screens (OCR MED).
  const asked = useRef(false);
  const answered = useRef(false);
  // Stripe bounces the buyer back regardless of app state, and the session can
  // still be restoring when they land (mobile Safari cold restore, password-
  // manager lag). Polling before `isLoaded` burns the whole 20s window on 401s
  // and then blames the webhook (review: JAR-1184). Wait for Clerk to settle
  // first; once settled we poll regardless of signed-in state — the backends
  // behind entitlement differ (demo mode has no Clerk session at all).
  const { isLoaded: sessionSettled } = useAuth();
  // What they held before checkout. Read at mount and left in storage until the
  // purchase lands, so a refresh of this screen reads it again rather than
  // falling back to the first-purchase rule.
  const [before] = useState(readEntitlementBeforeCheckout);

  // The intent has been acted on either way. A visitor who cancels and then
  // signs up again should not find the plan they declined chosen for them.
  // A canceled checkout bought nothing either, so nothing is waiting to land.
  useEffect(() => {
    clearPlanIntent();
    if (outcome === 'canceled') forgetEntitlementBeforeCheckout();
  }, [outcome]);

  useEffect(
    () => subscribeBackendStatus(() => setOutage(getBackendStatus() === 'offline')),
    [],
  );

  // The deadline is WALL-CLOCK and armed at MOUNT, outside the session gate
  // below. Two review rounds moved it here: checked only after a response
  // resolved, a hung request left the buyer on "Setting up your account"
  // indefinitely (Hermes MED); armed inside the gated effect, a Clerk that
  // never settles (script blocked, extension, provider down) registered no
  // timer at all, and the same buyer sat on a screen with no action (OCR MED).
  // Entitlement and outage both render ahead of stillWaiting, so firing it
  // after either has resolved changes nothing on screen.
  useEffect(() => {
    if (outcome !== 'success') return;
    const id = window.setTimeout(() => {
      if (asked.current && !answered.current) setOutage(true);
      setStillWaiting(true);
    }, POLL_WINDOW_MS);
    return () => window.clearTimeout(id);
  }, [outcome]);

  useEffect(() => {
    if (outcome !== 'success' || !sessionSettled) return;

    let cancelled = false;

    const poll = async () => {
      // getEntitlement never rejects — it fails closed to { tier: 'free' } —
      // so the .catch that used to sit here was dead, and a backend OUTAGE
      // arrived as "free, keep polling" and was then reported as webhook lag.
      // The reachability store is the one thing that can tell them apart, and
      // it is read AFTER the request so it describes this attempt. An outage
      // does not END polling (OCR MED, #252): a backend that recovers inside
      // the window clears the outage screen on the next attempt, instead of
      // leaving a paid buyer looking at "can't reach our servers" for good.
      asked.current = true;
      const next = await getEntitlement();
      if (cancelled) return;
      answered.current = true;
      const offline = getBackendStatus() === 'offline';
      setOutage(offline);

      // The purchase has landed when the entitlement DIFFERS from the one held
      // before checkout. The rule used to be `tier !== 'free'`, which is only
      // true of a first purchase: a Trip Pass holder upgrading to Explore, or
      // buying a second pass, already has a tier that is not free, so the first
      // poll declared success and showed them the plan they had just paid to
      // leave (JAR-1698). Never while offline: fail-closed `free` is not an
      // answer there, and nothing else would be either.
      if (!offline && next && entitlementChanged(before, next)) {
        forgetEntitlementBeforeCheckout();
        setEntitlement(next);
        return;
      }

      if (Date.now() - mountedAt.current >= POLL_WINDOW_MS) {
        setStillWaiting(true);
        return;
      }
      timers.current.push(window.setTimeout(poll, POLL_INTERVAL_MS));
    };

    // getEntitlement is documented never to reject, and that is enforced only
    // by its own file. If it ever does, an unhandled rejection would end the
    // loop silently and leave the buyer on the action-less first screen; the
    // outage screen (nothing is lost, Continue) is the honest fallback (OCR LOW).
    poll().catch(() => {
      if (!cancelled) setOutage(true);
    });

    return () => {
      cancelled = true;
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, [outcome, sessionSettled, before]);

  if (outcome === 'success' && outage) {
    return (
      <Shell
        icon={<Clock className="w-6 h-6 text-amber-600" aria-hidden="true" />}
        title="Your payment went through"
        body="We can't reach our servers right now, so we can't show your new plan yet. Nothing is lost. It will be there once we're back."
        action={<Button onClick={onContinue}>Continue to your trips</Button>}
      />
    );
  }

  if (outcome === 'canceled') {
    return (
      <Shell
        icon={<ArrowRight className="w-6 h-6 text-gray-500" aria-hidden="true" />}
        title="No payment taken"
        body="You closed the payment page, so nothing was charged. Your plan is still there when you want it."
        action={<Button onClick={onContinue}>Continue to your trips</Button>}
      />
    );
  }

  if (entitlement) {
    return (
      <Shell
        icon={<Check className="w-6 h-6 text-emerald-600" aria-hidden="true" />}
        title="You're all set"
        body={
          entitlement.tier === 'subscriber'
            ? 'Your subscription is active. Everything is unlocked.'
            : 'Your Trip Pass is ready. Time to plan something.'
        }
        action={<Button onClick={onContinue}>Start planning</Button>}
      />
    );
  }

  // Paid, but the grant has not arrived. Say exactly that. Claiming failure
  // here would be false, and claiming success would be a promise the app
  // cannot yet keep.
  if (stillWaiting) {
    return (
      <Shell
        icon={<Clock className="w-6 h-6 text-amber-600" aria-hidden="true" />}
        title="Payment received"
        body="Your payment went through and your account is still catching up. This usually takes a moment. If it does not clear, sign out and back in with the account you just created."
        action={<Button onClick={onContinue}>Continue to your trips</Button>}
      />
    );
  }

  return (
    <Shell
      icon={<Check className="w-6 h-6 text-emerald-600" aria-hidden="true" />}
      title="Payment received"
      body="Setting up your account."
      action={null}
    />
  );
}

function Shell({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <div className="min-h-dvh flex items-center justify-center px-6 bg-white">
      {/* aria-live so the "setting up" to "all set" transition is announced;
          a screen-reader user would otherwise sit on a silent page while the
          webhook lands. */}
      <div className="max-w-sm w-full text-center" role="status" aria-live="polite">
        <div className="mx-auto mb-5 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          {icon}
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600 mb-8 leading-relaxed">{body}</p>
        {action}
      </div>
    </div>
  );
}
